// Roles the user detail screen would offer for a given user, so the users
// table can tell whether a user really holds "all permissions".
//
// This rule mirrors `UserDetailScreen.permissionsOptions()` and the `ROLES_*`
// lists in `UserDetailScreen.js`. Keep both in sync when roles change.

const HIDDEN_BASE_ROLE = 4;
const COMPATIBILITY_ROLES = [17, 18];
const NAWI_ROLE_MACHINE_NAME = 'nawi';

const ACCOUNT_BASE_ROLES = [4, 6, 9, 10, 16, ...COMPATIBILITY_ROLES];
const ADMIN_EXCLUDED_ROLES = [5, 7, 8, 15, 16];
const ACCOUNT_SUPER_DASHBOARD_EXCLUDED_ROLES = [5, 7, 8, 15];
const ACCOUNT_REGULAR_EXCLUDED_ROLES = [15, 16];
const BRANCH_EXCLUDED_ROLES = [10, 15];

const isNawi = (role) => role.machine_name === NAWI_ROLE_MACHINE_NAME;

export const getAssignableRoles = ({ roles, viewer, target }) => {
  const catalog = roles || [];
  const isSuperadmin = !!viewer?.user_admin;
  const company = target?.nwm_user_company?.[0];

  const accountBase = () =>
    catalog.filter(
      (role) =>
        ACCOUNT_BASE_ROLES.includes(role.id) || (isSuperadmin && isNawi(role)),
    );
  const without = (list, excluded) =>
    list.filter((role) => !excluded.includes(role.id));

  let options;
  if (target?.nwm_user_admin?.length) {
    options = without(accountBase(), ADMIN_EXCLUDED_ROLES);
  } else if (company?.nwm_user_company_x_account?.length) {
    const viewerIsRegularAccount =
      viewer?.account && !viewer?.partner?.is_super_dashboard;
    options = without(
      accountBase(),
      viewerIsRegularAccount
        ? ACCOUNT_REGULAR_EXCLUDED_ROLES
        : ACCOUNT_SUPER_DASHBOARD_EXCLUDED_ROLES,
    );
  } else if (company?.nwm_user_company_x_branch?.length) {
    options = without(catalog, BRANCH_EXCLUDED_ROLES);
  } else {
    return [];
  }

  // Protected roles are only offered to superadmins.
  if (!isSuperadmin) {
    options = options.filter(
      (role) => !COMPATIBILITY_ROLES.includes(role.id) && !isNawi(role),
    );
  }
  return without(options, [HIDDEN_BASE_ROLE]);
};

export const hasAllAssignableRoles = (userRoleIds, assignableRoles) => {
  if (!assignableRoles?.length) return false;
  const held = new Set(userRoleIds);
  return assignableRoles.every(({ id }) => held.has(id));
};

// Same order as the user detail screen (`PERMISSION_ORDER_WEIGHTS`): by id,
// with the compatibility roles after role 6 and before role 7.
const DISPLAY_ORDER_WEIGHTS = { 17: 6.5, 18: 6.6 };
const displayWeight = ({ id }) => DISPLAY_ORDER_WEIGHTS[id] ?? id;

export const sortRolesByDisplayOrder = (roles) =>
  [...roles].sort(
    (role1, role2) => displayWeight(role1) - displayWeight(role2),
  );
